import React, { useEffect, useState, useCallback } from "react";
import { BookCheck, RefreshCw, ChevronDown, ChevronRight, History, CheckCircle2, XCircle, Clock } from "lucide-react";
import api from "@/lib/api";
import { AdminShell } from "@/components/AdminSidebar";

const TABS = [
  { key: "APPROVED", label: "Adopted" },
  { key: "PROPOSED", label: "Awaiting approval" },
  { key: "REJECTED", label: "Rejected" },
  { key: "", label: "All" },
];
const STATUS = {
  APPROVED: { cls: "text-race-volt border-race-volt/40", Icon: CheckCircle2 },
  PROPOSED: { cls: "text-amber-400 border-amber-400/40", Icon: Clock },
  REJECTED: { cls: "text-race-sub border-race-line", Icon: XCircle },
};

export default function SopLibrary() {
  const [tab, setTab] = useState("APPROVED");
  const [sops, setSops] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(null);

  const load = useCallback(() => {
    setLoading(true);
    api.get(`/agent-learning/sops${tab ? `?status=${tab}` : ""}`)
      .then((r) => setSops(r.data)).catch(() => setSops([])).finally(() => setLoading(false));
  }, [tab]);

  useEffect(() => { load(); }, [load]);

  const decide = (id, decision) =>
    api.post(`/agent-learning/sops/${id}/decision`, { decision }).then(load).catch(() => {});

  return (
    <AdminShell>
      <div className="animate-slide-up" data-testid="sop-library-page">
      <div className="flex items-center justify-between gap-3 flex-wrap mb-1">
        <div className="flex items-center gap-2">
          <BookCheck size={20} className="text-race-red" />
          <h1 className="font-heading text-3xl">SOP Library</h1>
        </div>
        <button onClick={load} data-testid="sop-refresh" className="flex items-center gap-1.5 text-sm border border-race-line hover:border-race-txt rounded-sm px-3 py-1.5 transition-colors">
          <RefreshCw size={14} className={loading ? "animate-spin" : ""} /> Refresh
        </button>
      </div>
      <p className="text-race-sub text-sm mb-6">Standard Operating Procedures adopted by Prime from recurring failure classes. Versioned and auditable.</p>

      <div className="flex flex-wrap gap-2 mb-6" data-testid="sop-tabs">
        {TABS.map((t) => (
          <button key={t.key} onClick={() => setTab(t.key)} data-testid={`sop-tab-${t.key || "all"}`}
            className={`text-xs font-semibold uppercase tracking-wide rounded-sm px-3 py-1.5 border transition-colors ${tab === t.key ? "border-race-red text-race-red" : "border-race-line text-race-sub hover:border-race-txt"}`}>
            {t.label}
          </button>
        ))}
      </div>

      {!loading && sops.length === 0 && (
        <div className="border border-race-line rounded-md p-8 text-center text-race-sub text-sm" data-testid="sop-empty">
          <BookCheck size={22} className="text-race-sub mx-auto mb-2" />
          No SOPs here yet. Prime drafts one automatically when a failure class recurs 3+ times, then routes it here for approval.
        </div>
      )}

      <div className="space-y-3" data-testid="sop-list">
        {sops.map((s) => {
          const st = STATUS[s.status] || STATUS.PROPOSED;
          return (
            <div key={s.sop_id} className="bg-race-surface border border-race-line rounded-md" data-testid={`sop-${s.sop_id}`}>
              <div className="px-4 py-3">
                <div className="flex items-center justify-between gap-3 flex-wrap">
                  <div className="min-w-0">
                    <div className="font-mono text-[11px] text-race-sub">{s.sop_id} · v{s.version || 1} · {s.occurrences}× · {s.agent_group}</div>
                    <div className="font-medium">{s.title}</div>
                  </div>
                  <span className={`flex items-center gap-1 text-[10px] font-mono uppercase border rounded-full px-2 py-0.5 ${st.cls}`}>
                    <st.Icon size={11} /> {s.status}
                  </span>
                </div>
                <ul className="mt-2 space-y-1">
                  {s.steps?.map((step, i) => <li key={i} className="text-xs text-race-sub flex gap-2"><span className="text-race-red">{i + 1}.</span>{step}</li>)}
                </ul>
                {s.related_bug_ids?.length > 0 && (
                  <div className="mt-2 text-[11px] font-mono text-race-sub">Source bugs: {s.related_bug_ids.join(", ")}</div>
                )}
                {s.decided_by && (
                  <div className="mt-1 text-[11px] text-race-sub">{s.status === "APPROVED" ? "Approved" : "Decided"} by {s.decided_by} · {s.decided_at?.slice(0, 10)}</div>
                )}
                <div className="flex items-center gap-2 mt-3">
                  {s.status === "PROPOSED" && (
                    <>
                      <button onClick={() => decide(s.sop_id, "approved")} data-testid={`sop-approve-${s.sop_id}`}
                        className="text-xs font-semibold bg-race-volt/90 text-race-bg rounded-sm px-3 py-1.5">Approve</button>
                      <button onClick={() => decide(s.sop_id, "rejected")} data-testid={`sop-reject-${s.sop_id}`}
                        className="text-xs font-semibold border border-race-line hover:border-race-red text-race-sub rounded-sm px-3 py-1.5">Reject</button>
                    </>
                  )}
                  {s.history?.length > 0 && (
                    <button onClick={() => setOpen(open === s.sop_id ? null : s.sop_id)} data-testid={`sop-history-${s.sop_id}`}
                      className="flex items-center gap-1 text-xs text-race-sub hover:text-race-txt ml-auto">
                      <History size={13} /> Version history {open === s.sop_id ? <ChevronDown size={13} /> : <ChevronRight size={13} />}
                    </button>
                  )}
                </div>
              </div>
              {open === s.sop_id && s.history?.length > 0 && (
                <div className="border-t border-race-line px-4 py-2 space-y-1" data-testid={`sop-history-panel-${s.sop_id}`}>
                  {s.history.map((h, i) => (
                    <div key={i} className="text-[11px] text-race-sub flex gap-2">
                      <span className="font-mono text-race-txt">v{h.version}</span>
                      <span className="uppercase">{h.event}</span>
                      <span>{h.actor || ""}</span>
                      <span className="ml-auto">{h.at?.slice(0, 16).replace("T", " ")}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>
      </div>
    </AdminShell>
  );
}
